import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  TextField,
  Button,
  IconButton,
  Divider,
  Stack,
  styled
} from '@mui/material';
import { LinkedIn, Facebook } from '@mui/icons-material';

const StyledFooter = styled(Box)(({ theme }) => ({
  backgroundColor: '#111827', // gray-900
  color: 'white',
  paddingTop: theme.spacing(8),
  paddingBottom: theme.spacing(4)
}));

const FooterLink = styled(Link)({
  color: 'rgba(156, 163, 175, 1)', // gray-400
  textDecoration: 'none',
  '&:hover': {
    color: 'white'
  }
});

const SocialButton = styled(IconButton)({
  color: 'rgba(156, 163, 175, 1)', // gray-400
  '&:hover': {
    color: '#0d9488'
  }
});

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-root': {
    backgroundColor: '#1F2937', // gray-800
    color: 'white',
    '&:hover': {
      backgroundColor: '#374151' // gray-700
    }
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(75, 85, 99, 1)' // gray-600
  },
  '& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#0d9488'
  },
  '& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#0d9488'
  }
}));

const Footer = ({ 
  onOpenPrivacyPolicy, 
  onOpenDocumentation, 
  onOpenSupport, 
  onOpenTermsOfService,
  onOpenAboutUs,
  onOpenCareers,
  onOpenBlog,
  onOpenContact
}) => {

  const currentYear = new Date().getFullYear();

  const footerSections = {
    product: {
      title: 'Product',
      links: [
        { text: 'Features', href: '#features' },
        { text: 'Pricing', href: '#pricing' },
        { text: 'Demo', href: '#demo' },
        { text: 'How it Works', href: '#how' }
      ]
    },
    company: {
      title: 'Company',
      links: [
        { text: 'About Us', onClick: onOpenAboutUs },
        { text: 'Careers', onClick: onOpenCareers },
        { text: 'Blog', onClick: onOpenBlog },
        { text: 'Contact', onClick: onOpenContact }
      ]
    },
    resources: {
      title: 'Resources',
      links: [
        { text: 'Documentation', onClick: onOpenDocumentation },
        { text: 'Support', onClick: onOpenSupport },
        { text: 'Privacy Policy', onClick: onOpenPrivacyPolicy }, 
        { text: 'Terms of Service', onClick: onOpenTermsOfService }
      ]
    }
  };

  return (
    <StyledFooter component="footer">
      <Container>
        {/* Main Footer Content */}
        <Grid container spacing={4} sx={{ mb: 6 }}>
          {/* Brand Column */}
          <Grid item xs={12} md={6} lg={4}>
            <Box sx={{ mb: 3 }}>
              <img
                src="/logo.svg"
                alt="Searcheble.com Logo"
                style={{ height: '32px' }}
              />
            </Box>

            <Stack direction="row" spacing={1}>
              <SocialButton aria-label="Facebook" >
                <Facebook />
              </SocialButton>
              <SocialButton aria-label="LinkedIn">
                <LinkedIn />
              </SocialButton>
            </Stack>
          </Grid>

          {/* Footer Sections */}
          {Object.entries(footerSections).map(([key, section]) => (
            <Grid item xs={12} sm={6} md={6} lg={2} key={key}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600}}>
                {section.title}
              </Typography>
              <Stack spacing={1.5} sx={{alignItems: 'flex-start'}}>
                {section.links.map((link) => (
                  link.href ? (
                    <FooterLink href={link.href} key={link.text}>
                      {link.text}
                    </FooterLink>
                  ) : (
                    <FooterLink 
                      component="button" 
                      onClick={(e) => {
                        e.preventDefault();
                        link.onClick();
                      }} 
                      key={link.text}
                    >
                      {link.text}
                    </FooterLink>
                  )
                ))}
              </Stack>
            </Grid>
          ))}

          {/* Newsletter Section
          <Grid item xs={12}>
            <Divider sx={{ borderColor: 'rgba(31, 41, 55, 1)', my: 4 }} />
            <Box sx={{ maxWidth: 'md', mx: 'auto', textAlign: 'center' }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Subscribe to our newsletter
              </Typography>
              <Typography variant="body2" sx={{ color: 'gray.400', mb: 2 }}>
                Get the latest updates and resources
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <StyledTextField
                  fullWidth
                  placeholder="Enter your email"
                  variant="outlined"
                  size="small"
                />
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: '#0d9488',
                    '&:hover': { bgcolor: 'rgba(13, 148, 136, 0.8)' }
                  }}
                >
                  Subscribe
                </Button>
              </Stack>
            </Box>
          </Grid> */}
        </Grid>

        {/* Bottom Bar */}
        <Divider sx={{ borderColor: 'rgba(31, 41, 55, 1)', mb: 4 }} />
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
        >
          <Typography variant="body2" sx={{ color: 'gray.400' }}>
            © {currentYear} Searcheble.com. All rights reserved.
          </Typography>
          <Stack direction="row" spacing={3}>
            {['Privacy Policy', 'Terms of Service', 'Cookie Policy'].map((text) => (
              text === 'Privacy Policy' ? (
                <FooterLink
                  key={text}
                  component="button"
                  onClick={(e) => {
                    e.preventDefault();
                    onOpenPrivacyPolicy();
                  }}
                >
                  {text}
                </FooterLink>
              ) : text === 'Terms of Service' ? (
                <FooterLink
                  key={text}
                  component="button"
                  onClick={(e) => {
                    e.preventDefault();
                    onOpenTermsOfService();
                  }}
                >
                  {text}
                </FooterLink>
              ) : (
                <FooterLink
                  key={text}
                  href={`/${text.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {text}
                </FooterLink>
              )
            ))}
          </Stack>
        </Stack>
      </Container>
    </StyledFooter>
  );
};

export default Footer;